<template>
  <!-- Wideo-instrukcja obsługi: lista działów (dział bieżącego ekranu na górze) + odtwarzacz. -->
  <Teleport to="body">
    <div v-if="isVisible" class="manual-overlay" @click.self="close">
      <div class="ds-modal manual" role="dialog" aria-modal="true" :aria-label="t('manual-title')">
        <header class="ds-modal__head manual__head">
          <span>{{ t("manual-title") }}</span>
          <button type="button" class="ds-btn ds-btn--ghost" :title="t('close')" @click="close">×</button>
        </header>

        <section class="ds-modal__body manual__body">
          <p v-if="isLoading" class="manual__message">{{ t("manual-loading") }}</p>
          <p v-else-if="loadError" class="manual__message manual__message--error">{{ t("manual-load-error") }}</p>
          <p v-else-if="!sections.length" class="manual__message">{{ t("manual-empty") }}</p>

          <div v-else class="manual__layout">
            <nav class="manual__list" :aria-label="t('manual-sections')">
              <button
                v-for="section in sections"
                :key="section.key"
                type="button"
                class="manual__item"
                :class="{ 'is-active': section.key === selected?.key, 'is-current': section.key === currentKey }"
                @click="selected = section"
              >
                <span>{{ t(`manual-section-${section.key}`) }}</span>
                <small v-if="section.key === currentKey" class="manual__badge">{{ t("manual-current-screen") }}</small>
              </button>
            </nav>

            <div v-if="selected" class="manual__player">
              <!-- :key wymusza nowy element przy zmianie działu — inaczej przeglądarka gra dalej stary plik. -->
              <video
                :key="selected.key"
                class="manual__video"
                :src="videoUrl(selected)"
                controls
                preload="metadata"
              ></video>
              <div class="manual__actions">
                <strong>{{ t(`manual-section-${selected.key}`) }}</strong>
                <!-- target="_blank" jest konieczne: link w tej samej karcie odpala `beforeunload`, a ten
                     (auth.store → sendLeaveRequest) wysyła DELETE /login i kończy sesję. Karta pobierania
                     zamyka się sama (Content-Disposition: attachment). -->
                <a
                  class="ds-btn ds-btn--secondary"
                  :href="videoUrl(selected, true)"
                  target="_blank"
                  rel="noopener"
                >{{ t("manual-download") }}</a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import ManualService from "@/services/ManualService";
import { sectionForRoute, orderSections } from "@/config/manualSections";

const props = defineProps({
  isVisible: { type: Boolean, default: false },
});
const emit = defineEmits(["close"]);

const { t } = useI18n();
const route = useRoute();

const sections = ref([]);
const selected = ref(null);
const isLoading = ref(false);
const loadError = ref(false);

// Dział opisujący ekran, z którego otwarto instrukcję.
const currentKey = computed(() => sectionForRoute(route?.name));

// Lista jest pobierana przy każdym otwarciu — bilety do wideo są krótkotrwałe i związane z sesją.
async function load() {
  isLoading.value = true;
  loadError.value = false;
  try {
    sections.value = orderSections((await ManualService.getSections()) || [], currentKey.value);
    selected.value = sections.value[0] ?? null;
  } catch (e) {
    sections.value = [];
    selected.value = null;
    loadError.value = true;
  } finally {
    isLoading.value = false;
  }
}

function onKeydown(event) {
  if (event.key === "Escape") {
    close();
  }
}

watch(
  () => props.isVisible,
  (visible) => {
    if (visible) {
      window.addEventListener("keydown", onKeydown);
      load();
    } else {
      window.removeEventListener("keydown", onKeydown);
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => window.removeEventListener("keydown", onKeydown));

function videoUrl(section, download = false) {
  return ManualService.videoUrl(section, download);
}

function close() {
  emit("close");
}
</script>

<style scoped>
.manual-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-xl);
  background: rgba(0, 0, 0, 0.45);
}

.manual {
  display: flex;
  flex-direction: column;
  width: min(1200px, 100%);
  max-height: 100%;
  overflow: hidden;
}

.manual__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.manual__body {
  min-height: 0;
  overflow: auto;
}

.manual__message {
  margin: 0;
  color: var(--text-secondary);
}

.manual__message--error {
  color: var(--text-danger);
}

.manual__layout {
  display: grid;
  grid-template-columns: minmax(200px, 1fr) minmax(0, 3fr);
  gap: var(--spacing-lg);
  align-items: start;
}

.manual__list {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-default);
  border-radius: var(--control-radius);
  overflow: hidden;
}

.manual__item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-2xs);
  padding: var(--spacing-sm) var(--spacing-md);
  border: 0;
  background: var(--surface-primary);
  color: var(--text-primary);
  font-size: var(--font-size-sm);
  text-align: left;
  cursor: pointer;
}

.manual__item + .manual__item {
  border-top: 1px solid var(--border-subtle);
}

.manual__item:hover {
  background: var(--surface-hover);
}

.manual__item.is-current {
  background: var(--accent-blue-soft);
}

.manual__item.is-active {
  box-shadow: inset 3px 0 0 var(--surface-inverse);
  font-weight: var(--font-weight-bold);
}

.manual__badge {
  color: var(--accent-blue);
  font-size: var(--font-size-2xs);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
}

.manual__player {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  min-width: 0;
}

.manual__video {
  width: 100%;
  max-height: 70vh;
  background: var(--surface-inverse);
  border-radius: var(--control-radius);
}

.manual__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
}
</style>
